import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import type { CreateJobItemRequest } from "@/lib/jobs";

// ----------------------------------------------------------------------------
// Local draft row type — string fields for controlled inputs, committed via
// onChange callback which converts to the API shape.
// ----------------------------------------------------------------------------

export interface JobItemRow {
  /** Transient client-side key for React reconciliation */
  _key: string;
  sortOrder: string;
  description: string;
  quantity: string;
  unitPriceExGst: string;
  gstRate: string;
}

export function blankRow(sortOrder = 0): JobItemRow {
  return {
    _key: `${Date.now()}-${Math.random()}`,
    sortOrder: String(sortOrder),
    description: "",
    quantity: "1",
    unitPriceExGst: "0",
    gstRate: "0.1",
  };
}

/** Compute the line total including GST for display purposes. */
export function rowTotalIncGst(row: JobItemRow): number {
  const qty = Number(row.quantity);
  const price = Number(row.unitPriceExGst);
  const gst = Number(row.gstRate);
  if (!Number.isFinite(qty) || !Number.isFinite(price) || !Number.isFinite(gst)) return 0;
  return qty * price * (1 + gst);
}

/** Convert a draft row to a CreateJobItemRequest (undefined when invalid). */
export function rowToRequest(row: JobItemRow): CreateJobItemRequest | undefined {
  const description = row.description.trim();
  const quantity = Number(row.quantity);
  const unitPriceExGst = Number(row.unitPriceExGst);
  const gstRate = Number(row.gstRate);
  if (
    description.length === 0 ||
    !Number.isFinite(quantity) || quantity <= 0 ||
    !Number.isFinite(unitPriceExGst) || unitPriceExGst < 0 ||
    !Number.isFinite(gstRate) || gstRate < 0
  ) {
    return undefined;
  }
  return { description, quantity, unitPriceExGst, gstRate, sortOrder: Number(row.sortOrder) };
}

// ----------------------------------------------------------------------------
// Currency formatter
// ----------------------------------------------------------------------------

const fmt = new Intl.NumberFormat("en-AU", { style: "currency", currency: "AUD" });

// ----------------------------------------------------------------------------
// Component
// ----------------------------------------------------------------------------

export interface JobItemsGridProps {
  items: JobItemRow[];
  onChange: (items: JobItemRow[]) => void;
  editable?: boolean;
}

export function JobItemsGrid({ items, onChange, editable = true }: JobItemsGridProps) {
  function updateRow(index: number, patch: Partial<JobItemRow>) {
    onChange(items.map((row, i) => (i === index ? { ...row, ...patch } : row)));
  }

  function addRow() {
    onChange([...items, blankRow(items.length)]);
  }

  function removeRow(index: number) {
    onChange(items.filter((_, i) => i !== index));
  }

  return (
    <div className="space-y-2">
      <div className="overflow-x-auto rounded-md border">
        <table className="w-full text-sm" aria-label="Job items">
          <thead className="bg-muted/50">
            <tr>
              <th className="p-2 text-left">Description</th>
              <th className="p-2 text-right">Qty</th>
              <th className="p-2 text-right">Unit ex GST</th>
              <th className="p-2 text-right">GST rate</th>
              <th className="p-2 text-right">Total inc GST</th>
              {editable && <th className="p-2" />}
            </tr>
          </thead>
          <tbody>
            {items.length === 0 && (
              <tr>
                <td colSpan={editable ? 6 : 5} className="p-3 text-center text-sm text-muted-foreground">
                  No items yet.
                </td>
              </tr>
            )}
            {items.map((row, i) => (
              <tr key={row._key} className="border-t">
                <td className="p-1">
                  {editable ? (
                    <Input
                      aria-label={`Description ${i + 1}`}
                      value={row.description}
                      onChange={(e) => updateRow(i, { description: e.target.value })}
                    />
                  ) : (
                    <span>{row.description}</span>
                  )}
                </td>
                <td className="p-1">
                  {editable ? (
                    <Input
                      aria-label={`Quantity ${i + 1}`}
                      type="number"
                      className="w-20"
                      value={row.quantity}
                      onChange={(e) => updateRow(i, { quantity: e.target.value })}
                    />
                  ) : (
                    <span className="block text-right">{row.quantity}</span>
                  )}
                </td>
                <td className="p-1">
                  {editable ? (
                    <Input
                      aria-label={`Unit price ${i + 1}`}
                      type="number"
                      className="w-28"
                      value={row.unitPriceExGst}
                      onChange={(e) => updateRow(i, { unitPriceExGst: e.target.value })}
                    />
                  ) : (
                    <span className="block text-right">{fmt.format(Number(row.unitPriceExGst))}</span>
                  )}
                </td>
                <td className="p-1">
                  {editable ? (
                    <Input
                      aria-label={`GST rate ${i + 1}`}
                      type="number"
                      className="w-20"
                      value={row.gstRate}
                      onChange={(e) => updateRow(i, { gstRate: e.target.value })}
                    />
                  ) : (
                    <span className="block text-right">{row.gstRate}</span>
                  )}
                </td>
                <td className="p-1 text-right">
                  {fmt.format(rowTotalIncGst(row))}
                </td>
                {editable && (
                  <td className="p-1 text-center">
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => removeRow(i)}
                    >
                      Remove
                    </Button>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {editable && (
        <Button type="button" variant="outline" onClick={addRow}>
          Add item
        </Button>
      )}
    </div>
  );
}
